import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useChildren } from "@/context/ChildrenContext";
import api from "@/lib/api";
import { ADVICE, ACTIVITIES, COACH_PROMPTS } from "@/data/parentingData";
import { Sparkles, Trophy, NotebookPen, MessageCircle, ArrowRight } from "lucide-react";
import MoodChart from "@/components/MoodChart";
import DailyReminder from "@/components/DailyReminder";
import WeeklyChallenge from "@/components/WeeklyChallenge";
import Mascot from "@/components/Mascot";

const QUOTES = [
  "Un parent apaisé fabrique un enfant apaisé.",
  "Nommer une émotion, c'est déjà l'apprivoiser.",
  "Ton meilleur moi suffit à ton enfant.",
  "Les rituels sont les racines de la sécurité.",
  "Un câlin sincère vaut mille discours.",
];

function getAgeBucket(age) {
  if (age <= 2) return "0-2";
  if (age <= 5) return "3-5";
  if (age <= 9) return "6-9";
  if (age <= 12) return "10-12";
  return "13+";
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { activeChild } = useChildren();
  const [journalCount, setJournalCount] = useState(0);
  const [journal, setJournal] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const quote = QUOTES[new Date().getDate() % QUOTES.length];

  useEffect(() => {
    (async () => {
      const [j, c] = await Promise.all([api.get("/journal"), api.get("/challenges")]);
      setJournal(j.data);
      setJournalCount(j.data.length);
      setChallenges(c.data);
      setActiveChallenge(c.data[0] || null);
    })();
  }, []);

  const reloadChallenges = async () => {
    const { data } = await api.get("/challenges");
    setChallenges(data);
    setActiveChallenge(data[0] || null);
  };

  const bucket = activeChild ? getAgeBucket(activeChild.age) : "3-5";
  const tipPool = ADVICE.filter((a) => a.age === bucket);
  const dayIndex = Math.floor(Date.now() / 86400000);
  const dailyTip = tipPool.length ? tipPool[dayIndex % tipPool.length] : ADVICE[0];
  const activityPool = ACTIVITIES.filter((a) => a.age === bucket);
  const dailyActivity = activityPool.length
    ? activityPool[dayIndex % activityPool.length]
    : ACTIVITIES[dayIndex % ACTIVITIES.length];

  return (
    <div className="space-y-8" data-testid="dashboard-hero-section">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#E6F5EF] via-[#E3EAF7] to-[#FBE2E9] p-8 sm:p-10">
        <div className="relative z-10 sm:pr-40">
          <p className="text-xs font-bold uppercase tracking-wider text-[#16233D]">Bonjour {user?.name}</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            {activeChild ? `Une belle journée avec ${activeChild.name} ✨` : "Ajoute ton premier enfant pour commencer 👶"}
          </h1>
          <p data-testid="daily-quote" className="mt-3 text-lg text-[#16233D] italic">« {quote} »</p>
        </div>
        <div className="absolute right-4 bottom-0 hidden sm:block">
          <Mascot pose="thumbsup" size={150} anim="float" className="mascot-pop" />
        </div>
        <div className="mt-4 flex justify-center sm:hidden">
          <Mascot pose="thumbsup" size={120} anim="float" className="mascot-pop" />
        </div>
      </section>

      <DailyReminder challenges={challenges} />

      <WeeklyChallenge
        child={activeChild}
        activeTitles={challenges.map((c) => c.title)}
        onLaunched={reloadChallenges}
      />

      {/* Grid */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="soft-card p-6" data-testid="daily-tip-card">
          <div className="flex items-center gap-2 text-[#77849C] text-xs font-bold uppercase tracking-wider">
            <NotebookPen className="h-4 w-4" /> Conseil du jour
          </div>
          <h3 className="mt-3 text-xl font-semibold text-[#16233D]">{dailyTip.title}</h3>
          <span className="mt-2 age-badge pastel-blush">{dailyTip.age} ans · {dailyTip.category}</span>
          <p className="mt-3 text-[#44506B] leading-relaxed">{dailyTip.body}</p>
          <Link to="/conseils" className="mt-4 inline-flex items-center gap-1 text-[#23375E] font-medium hover:underline">
            Tous les conseils <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="soft-card p-6" data-testid="daily-activity-card">
          <div className="flex items-center gap-2 text-[#77849C] text-xs font-bold uppercase tracking-wider">
            <Sparkles className="h-4 w-4" /> Activité à faire
          </div>
          <h3 className="mt-3 text-xl font-semibold text-[#16233D]">{dailyActivity.title}</h3>
          <div className="mt-2 flex gap-2 flex-wrap">
            <span className="age-badge pastel-sage">{dailyActivity.type}</span>
            <span className="age-badge pastel-butter">{dailyActivity.duration}</span>
            <span className="age-badge pastel-sky">{dailyActivity.ages}</span>
          </div>
          <p className="mt-3 text-[#44506B] leading-relaxed line-clamp-3">{dailyActivity.steps[0]}</p>
          <Link to="/activites" className="mt-4 inline-flex items-center gap-1 text-[#23375E] font-medium hover:underline">
            Explorer <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="soft-card p-6" data-testid="challenge-card">
          <div className="flex items-center gap-2 text-[#77849C] text-xs font-bold uppercase tracking-wider">
            <Trophy className="h-4 w-4" /> Défi actif
          </div>
          {activeChallenge ? (
            <>
              <h3 className="mt-3 text-xl font-semibold text-[#16233D]">{activeChallenge.title}</h3>
              <p className="mt-2 text-[#44506B]">{activeChallenge.completed_days.length} / {activeChallenge.duration_days} jours accomplis 🎯</p>
              <div className="mt-3 h-2 bg-[#E3E8F1] rounded-full overflow-hidden">
                <div className="h-full bg-[#23375E]" style={{ width: `${(activeChallenge.completed_days.length / activeChallenge.duration_days) * 100}%` }} />
              </div>
            </>
          ) : (
            <>
              <h3 className="mt-3 text-xl font-semibold text-[#16233D]">Aucun défi en cours</h3>
              <p className="mt-2 text-[#44506B]">Lance un défi famille pour vivre de beaux moments ensemble.</p>
            </>
          )}
          <Link to="/defis" className="mt-4 inline-flex items-center gap-1 text-[#23375E] font-medium hover:underline">
            Défis famille <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Coach quick */}
      <section className="soft-card p-6 sm:p-8 pastel-lavender !bg-[#E3EAF7] relative overflow-hidden" data-testid="coach-quick-card">
        <div className="absolute right-3 -bottom-2 hidden md:block">
          <Mascot pose="idea" size={128} anim="wiggle" delay={400} />
        </div>
        <div className="relative z-10 md:pr-36">
        <div className="flex items-center gap-2 text-[#16233D]">
          <MessageCircle className="h-5 w-5" />
          <span className="text-xs font-bold uppercase tracking-wider">Coach IA parental</span>
        </div>
        <h3 className="mt-2 text-2xl font-semibold text-[#16233D]">Une question ? Le coach vous répond avec douceur.</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {COACH_PROMPTS.slice(0, 4).map((p) => (
            <Link key={p} to={`/coach?q=${encodeURIComponent(p)}`} data-testid={`prompt-chip-${p.substring(0,15)}`}
              className="rounded-full bg-white/70 px-4 py-2 text-sm text-[#16233D] hover:bg-white transition">
              {p}
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="soft-card p-4 text-center" data-testid="stat-journal">
          <p className="text-3xl font-bold text-[#23375E]">{journalCount}</p>
          <p className="text-sm text-[#77849C]">Entrées journal</p>
        </div>
        <div className="soft-card p-4 text-center">
          <p className="text-3xl font-bold text-[#23375E]">{activeChallenge?.completed_days.length ?? 0}</p>
          <p className="text-sm text-[#77849C]">Jours de défi</p>
        </div>
        <div className="soft-card p-4 text-center">
          <p className="text-3xl font-bold text-[#23375E]">{ACTIVITIES.length}</p>
          <p className="text-sm text-[#77849C]">Activités dispo</p>
        </div>
        <div className="soft-card p-4 text-center">
          <p className="text-3xl font-bold text-[#23375E]">{ADVICE.length}</p>
          <p className="text-sm text-[#77849C]">Conseils experts</p>
        </div>
      </section>

      {/* Mood chart */}
      <MoodChart entries={journal} />
    </div>
  );
}
